import aircrafts from '../assets/aircraftList/aircrafts.json';
import airports from '../assets/airportList/airports.json';
import stringSimilarity from 'string-similarity';

// React-friendly AutoFix (mirroring RN as closely as possible)
export default async function AutoFix(tableData) {
  try {
    // Retrieve masterTableTemplate from sessionStorage (RN uses AsyncStorage)
    const storedTemplate = sessionStorage.getItem('masterTableTemplate');
    if (storedTemplate !== null) {
      const masterTableTemplate = JSON.parse(storedTemplate);

      const headers = tableData.data[0]; // first row is header
      const newTableData = makeAssumptions(headers, masterTableTemplate, tableData);

      return newTableData;
    } else {
      console.error('No masterTableTemplate found in sessionStorage');
      return null;
    }
  } catch (error) {
    console.error('Error retrieving masterTableTemplate from sessionStorage:', error);
    return null;
  }
}

function makeAssumptions(headers, masterTableTemplate, tableData) {
  const headerRowCount = 1;  // Assume one header row

  masterTableTemplate.forEach((columnTemplate, index) => {
    const colIndex = columnTemplate.index - 1; // Adjust for 0-based index
    const header = columnTemplate.name.toLowerCase();
    let assumptionsMade = false;

    // Aircraft Make & Model
    if (columnTemplate.name === "Aircraft Make & Model") {
      assumptionsMade = true;
      for (let rowIndex = headerRowCount; rowIndex < tableData.data.length; rowIndex++) {
        const row = tableData.data[rowIndex];
        if (row[colIndex] !== null && row[colIndex] !== undefined) {
          let highestSimilarityAircraft = '';
          let highestSimilarityValue = 0;
          for (let i = 0; i < aircrafts.Aircrafts.length; i++) {
            const similarity = stringSimilarity.compareTwoStrings(row[colIndex].trim(), aircrafts.Aircrafts[i]);
            if (similarity >= 0.40 && similarity > highestSimilarityValue) {
              highestSimilarityAircraft = aircrafts.Aircrafts[i];
              highestSimilarityValue = similarity;
            }
          }
          if (highestSimilarityAircraft !== '') {
            row[colIndex] = highestSimilarityAircraft;
          }
        }
      }
    }

    // Departure (From)
    if (columnTemplate.name === "From") {
      assumptionsMade = true;
      for (let rowIndex = headerRowCount; rowIndex < tableData.data.length; rowIndex++) {
        const row = tableData.data[rowIndex];
        if (row[colIndex] !== null && row[colIndex] !== undefined) {
          const cellValue = row[colIndex].trim();
          if (cellValue.length === 4) {
            let highestSimilarityAirport = '';
            let highestSimilarityValue = 0;
            for (let i = 0; i < airports.Airports.length; i++) {
              const similarity = stringSimilarity.compareTwoStrings(cellValue, airports.Airports[i]);
              if (similarity >= 0.20 && similarity > highestSimilarityValue) {
                highestSimilarityAirport = airports.Airports[i];
                highestSimilarityValue = similarity;
              }
            }
            if (highestSimilarityAirport !== '') {
              row[colIndex] = highestSimilarityAirport;
            }
          }
        }
      }
    }

    // Arrival (To)
    if (columnTemplate.name === "To") {
      assumptionsMade = true;
      for (let rowIndex = headerRowCount; rowIndex < tableData.data.length; rowIndex++) {
        const row = tableData.data[rowIndex];
        if (row[colIndex] !== null && row[colIndex] !== undefined) {
          const cellValue = row[colIndex].trim();
          if (cellValue.length === 4) {
            let highestSimilarityAirport = '';
            let highestSimilarityValue = 0;
            for (let i = 0; i < airports.Airports.length; i++) {
              const similarity = stringSimilarity.compareTwoStrings(cellValue, airports.Airports[i]);
              if (similarity >= 0.20 && similarity > highestSimilarityValue) {
                highestSimilarityAirport = airports.Airports[i];
                highestSimilarityValue = similarity;
              }
            }
            if (highestSimilarityAirport !== '') {
              row[colIndex] = highestSimilarityAirport;
            }
          }
        }
      }
    }

    // Date column
    if (columnTemplate.name === "Date") {
      let yearInHeader = new Date().getFullYear();
      const yearMatch = header.match(/\d{4}/);
      if (yearMatch) {
        yearInHeader = parseInt(yearMatch[0]);
      }
      let prevDate = null;

      for (let rowIndex = headerRowCount; rowIndex < tableData.data.length; rowIndex++) {
        const row = tableData.data[rowIndex];
        if (!row[colIndex]) continue;

        const dateValue = row[colIndex].trim();

        // Match MM/DD or MM/DD/YY or MM/DD/YYYY
        const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);

        if (match) {
          const month = parseInt(match[1], 10);
          const day = parseInt(match[2], 10);
          let year = match[3] 
            ? (match[3].length === 2 ? 2000 + parseInt(match[3], 10) : parseInt(match[3], 10))
            : yearInHeader;

          const potentialDate = new Date(year, month - 1, day);

          if (!isNaN(potentialDate.getTime())) {
            row[colIndex] = `${month}/${day}/${potentialDate.getFullYear().toString().slice(-2)}`;
            prevDate = potentialDate;
            continue;
          }
        }

        // If invalid but we had a previous date, increment it
        if (prevDate) {
          prevDate = new Date(prevDate.getTime());
          prevDate.setDate(prevDate.getDate() + 1);
          row[colIndex] = `${prevDate.getMonth() + 1}/${prevDate.getDate()}/${prevDate.getFullYear().toString().slice(-2)}`;
          continue;
        }

        // Last fallback: look forward
        const nextDate = getNextDate(tableData.data, rowIndex, colIndex);
        if (nextDate) {
          nextDate.setDate(nextDate.getDate() - 1);
          row[colIndex] = `${nextDate.getMonth() + 1}/${nextDate.getDate()}/${nextDate.getFullYear().toString().slice(-2)}`;
        } else {
          // If all else fails, leave it blank
          row[colIndex] = "";
        }
      }
    }

    // Numeric columns
    if (columnTemplate.dataType === "number") {
      for (let rowIndex = headerRowCount; rowIndex < tableData.data.length; rowIndex++) {
        const row = tableData.data[rowIndex];
        let originalValue = row[colIndex];

        if (originalValue === null || originalValue === undefined || originalValue === "") {
          row[colIndex] = "";
          continue;
        }

        let cleanedValue = String(originalValue).trim();
        cleanedValue = correctMistakenCharacters(cleanedValue);

        if (/^\d+ \d+$/.test(cleanedValue)) {
          cleanedValue = cleanedValue.replace(" ", ".");
        } else if (/^0\d$/.test(cleanedValue)) {
          cleanedValue = `0.${cleanedValue[1]}`;
        }

        let parsedValue = parseFloat(cleanedValue);
        let updatedValue = "";

        if (isNaN(parsedValue)) {
          row[colIndex] = "";
          continue;
        }

        if (columnTemplate.numberFormat === "integer") {
          updatedValue = Math.round(parsedValue);
        } else if (columnTemplate.numberFormat === "decimal") {
          if (parsedValue >= 10) {
            parsedValue = parsedValue / 10;
          }
          updatedValue = parsedValue.toFixed(1);
        }

        row[colIndex] = updatedValue;
      }
    }
  });

  return tableData;
}

function correctMistakenCharacters(value) {
  const mistakenValueMappings = {
    'o': '0','O': '0','I': '1','l': '1','|': '1','/': '1','\\': '1',
    'C': '0','c': '0','S': '5','s': '5','Z': '2','z': '2','B': '8','b': '8',
    'G': '6','g': '6','T': '7','t': '7','D': '0','d': '0','Q': '0','q': '0',
    'J': '1','j': '1','H': '4','h': '4','E': '3','e': '3','R': '2','r': '2',
    'L': '1','K': '4','k': '4','M': '4','m': '4','N': '1','n': '1','U': '0',
    'u': '0','V': '4','v': '4','W': '4','w': '4','X': '1','x': '1','Y': '1',
    'y': '1','A': '4','a': '4','F': '4','f': '4','P': '0','p': '0','@': '2',
    '#': '4','*': '3','&': '8','!': '1','?': '7','+': '7',' ': ''
  };
  return value.split('').map(char => mistakenValueMappings[char] || char).join('');
}

function getNextDate(data, rowIndex, colIndex) {
  for (let i = rowIndex + 1; i < data.length; i++) {
    const potentialDate = new Date(data[i][colIndex]);
    if (!isNaN(potentialDate.getTime())) {
      return potentialDate;
    }
  }
  return null;
}
